// Third-party libraries
import { CssBaseline, ThemeProvider } from "@mui/material";
import { Routes, Route, Navigate } from "react-router-dom";
import "leaflet/dist/leaflet.css";

// Theme
import { ColorModeContext, useMode } from "../src/theme/index";

// Navigation
import Layout from "./navigation/Layout";

// Components
import ProtectedRoute from "./auth/ProtectedRoute";
import EditTaskForm from "./views/manageTasks/EditTaskForm";
import EditCompanyForm from "./views/manageCompanies/EditCompanyForm.jsx";
import EditAuditQuestionForm from "./views/manageAuditQuestions/EditAuditQuestionsForm.jsx";
import EditOperationalAuditForm from "./views/manageOperationalAudits/EditOperationalAuditsForm.jsx";
import EditCompanyAddressForm from "./views/manageCompanyAddresses/EditCompanyAddressForm.jsx";
import EditProjectForm from "./views/manageProjects/EditProjectForm";
import EditSOPForm from "./views/manageSOPs/EditSOPForm";
import EditUserForm from "./views/manageUsers/EditUserForm";
import EditStaffWorkPatternForm from "./views/manageStaffWorkPatterns/EditStaffWorkPatternForm";
import EditTenantForm from "./views/manageTenants/EditTenantForm";
import EditContactForm from "./views/manageContacts/EditContactForm";
import EditInteractionForm from "./views/manageInteractions/EditInteractionForm";
import EditDepartmentForm from "./views/manageDepartments/EditDepartmentForm";
import EditPublicHolidayForm from "./views/manageHolidays/EditPublicHolidayForm";
import EditTenantRoleForm from "./views/manageTenantRoles/EditTenantRoleForm";
import EditTenantUserForm from "./views/manageTenantUsers/EditTenantUserForm";
import EditPlaybookForm from "./views/managePlaybooks/EditPlaybookForm";

// Public
import Login from "./auth/LoginPanel";
import TenantLoginPage from "./auth/TenantLoginPage.jsx";
import LandingPage from "./views/landingPage";
import ShirtLandingPage from "./views/landingPage/shirt";
import FAQ from "./views/faq";
import About from "./views/about";
import Insights from "./views/insights";
import Contact from "./views/contact";

// Views 
import Dashboard from "./views/dashboard";
import RoleAllocation from "./views/roleAllocation";
import ClientAdminDashboard from "./views/ClientAdminDashboard";
import OperationalClarityDashboard from "./views/ClientAdminDashboard/OperationalClarityDashboard";
import KnowledgeCoverageDashboard from "./views/ClientAdminDashboard/KnowledgeCoverageDashboard";
import RunOperationalAudit from "./views/runOperationalAudit";
import ViewPlaybook from "./views/managePlaybooks/ViewPlaybook";

// Views - Admin
import ManageUsers from "./views/manageUsers";
import ManageStaffWorkPatterns from "./views/manageStaffWorkPatterns";
import ManageContacts from "./views/manageContacts";
import ManageCompanies from "./views/manageCompanies";
import ManageAuditQuestions from "./views/manageAuditQuestions";
import ManageOperationalAudits from "./views/manageOperationalAudits/index.jsx";
import ManageCompanyAddresses from "./views/manageCompanyAddresses";
import ManageTenants from "./views/manageTenants";
import ManageInteractions from "./views/manageInteractions";
import ManageTasks from "./views/manageTasks";
import ManageProjects from "./views/manageProjects";
import ManageTenantRoles from "./views/manageTenantRoles";
import ManageTenantUsers from "./views/manageTenantUsers";
import ManageDepartments from "./views/manageDepartments";
import ManageBusinessStructure from "./views/manageBusinessStructure";
import ManagePublicHolidays from "./views/manageHolidays";
import ManagePlaybooks from "./views/managePlaybooks";
import ManageSOPs from "./views/manageSOPs";
import KanbanBoard from "./views/kanban";
import CalendarView from "./views/kanban/calendar";

import CreateUser from "./views/createUser";
import CreateStaffWorkPattern from "./views/createWorkPattern";
import CreateContact from "./views/createContact";
import CreateAuditQuestions from "./views/createAuditQuestions";
import CreateOperationalAudits from "./views/createOperationalAudits";
import EventContactCapture from "./views/createContact/event";
import CreateCompanyAddress from "./views/createCompanyAddress";
import CreatePublicHoliday from "./views/createPublicHolidays";
import CreateCompany from "./views/createCompany";
import CreateTask from "./views/createTask";
import CreateProject from "./views/createProject";
import CreateInteraction from "./views/createInteraction";
import CreateDepartment from "./views/createDepartment";
import CreateTenantRole from "./views/createTenantRole";
import CreateTenantUser from "./views/createTenantUser";
import CreatePlaybook from "./views/createPlaybook";

import ManageMeeting from "./views/manageMeeting";

// Staff
import StaffRoles from "./views/staffRoles";
import StaffDashboard from "./views/StaffDashboard";
import MyWorkPatterns from "./views/staffCalendar";

// SOPs
import SopLibrary from "./views/sops/SopLibrary";
import SopReader from "./views/sops/SopReader";
import MySops from "./views/sops/MySops";
import MySOPApprovalsView from "./views/sops/tenantStaffSOPApprovals";
import TenantAdminSOPApprovals from "./views/sops/tenantAdminSOPApprovals";
import RejectedSOPApprovalsView from "./views/sops/RejectedSOPApprovalsView.jsx";

// Project Cost View
import ProjectCostView from "./views/manageProjects/ProjectsCostsView.jsx"

import { UserRole } from "./constants/userRoles.ts";
import { SnackbarProvider } from "./components/snackbarProvider.jsx";

function App() {
  const [theme, colourMode] = useMode();

  return (
    <ColorModeContext.Provider value={colourMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider>
        <Routes>
          {/* Standalone auth pages */}
          <Route path="/login" element={<Login />} />
          <Route path="/:tenantSlug/login" element={<TenantLoginPage />} />
          <Route path="/trackMeeting" element={<ManageMeeting />} />

          {/* Public pages with layout */}
          <Route element={<Layout />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/about" element={<About />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/shirt" element={<ShirtLandingPage />} />
          </Route>

          {/* Protected app pages with layout */}
          <Route element={<Layout />}>
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operational-clarity-dashboard"
              element={
                <ProtectedRoute role={[UserRole.TENANT_ADMIN, UserRole.SYSTEM_ADMIN]}>
                  <OperationalClarityDashboard />
                </ProtectedRoute>
              }
            />
             <Route
              path="/knowledge-coverage-dashboard"
              element={
                <ProtectedRoute role={[UserRole.TENANT_ADMIN, UserRole.SYSTEM_ADMIN]}>
                  <KnowledgeCoverageDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/kanban"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <KanbanBoard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <CalendarView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/roleAllocation"
              element={
                <ProtectedRoute role={[UserRole.TENANT_ADMIN, UserRole.SYSTEM_ADMIN]}>
                  <RoleAllocation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clientAdminDashboard"
              element={
                <ProtectedRoute role={[UserRole.TENANT_ADMIN, UserRole.SYSTEM_ADMIN]}>
                  <ClientAdminDashboard />
                </ProtectedRoute>
              }
            />
            
          {/* Manage Global Admin */}
            
            <Route
              path="/manageTenants"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <ManageTenants />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenants/edit/:id"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <EditTenantForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/createContact"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <CreateContact />
                </ProtectedRoute>
              }
            />
             <Route
              path="/createContact/event"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <EventContactCapture />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manageContacts"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <ManageContacts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contacts/edit/:id"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <EditContactForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/createCompany"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <CreateCompany />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manageCompanies"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <ManageCompanies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/companies/edit/:id"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <EditCompanyForm />
                </ProtectedRoute>
              }
            />
             <Route
              path="/createCompanyAddress"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <CreateCompanyAddress />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manageCompanyAddresses"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <ManageCompanyAddresses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/companyAddresses/edit/:id"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <EditCompanyAddressForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/createInteraction"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <CreateInteraction />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manageInteractions"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <ManageInteractions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/interactions/edit/:id"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <EditInteractionForm />
                </ProtectedRoute>
              }
            />
             <Route
              path="/createAuditQuestions"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <CreateAuditQuestions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manageAuditQuestions"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <ManageAuditQuestions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/auditQuestions/edit/:id"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <EditAuditQuestionForm />
                </ProtectedRoute>
              }
            />

             <Route
              path="/createOperationalAudits"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <CreateOperationalAudits />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manageOperationalAudits"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <ManageOperationalAudits />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operationalAudits/edit/:id"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <EditOperationalAuditForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operationalAudits/run/:id"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <RunOperationalAudit />
                </ProtectedRoute>
              }
            />

            {/* Tenant Configuration */}
            <Route
              path="/createPlaybook"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <CreatePlaybook />
                </ProtectedRoute>
              }
            />
            <Route
              path="/managePlaybooks"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <ManagePlaybooks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/playbooks/read/:id"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <ViewPlaybook />
                </ProtectedRoute>
              }
            />
            <Route
              path="/playbooks/edit/:id"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <EditPlaybookForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/createDepartment"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <CreateDepartment />
                </ProtectedRoute>
              }
            />

            <Route
              path="/manageDepartments"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <ManageDepartments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/departments/edit/:id"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <EditDepartmentForm />
                </ProtectedRoute>
              }
            />
            <Route
                path="/manageBusinessStructure"
                element={
                  <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                    <ManageBusinessStructure />
                  </ProtectedRoute>
                }
              />
             <Route
              path="/createTenantRole"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <CreateTenantRole />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manageTenantRoles"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <ManageTenantRoles />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenantRoles/edit/:id"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <EditTenantRoleForm />
                </ProtectedRoute>
              }
            />

            <Route
              path="/createUser"
              element={
                <ProtectedRoute role={[UserRole.TENANT_ADMIN, UserRole.SYSTEM_ADMIN]}>
                  <CreateUser />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manageUsers"
              element={
                <ProtectedRoute role={[UserRole.TENANT_ADMIN, UserRole.SYSTEM_ADMIN]}>
                  <ManageUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/edit/:id"
              element={
                <ProtectedRoute role={[UserRole.TENANT_ADMIN, UserRole.SYSTEM_ADMIN]}>
                  <EditUserForm />
                </ProtectedRoute>
              }
            />

              <Route
              path="/createStaffWorkPattern"
              element={
                <ProtectedRoute role={[UserRole.TENANT_ADMIN, UserRole.SYSTEM_ADMIN]}>
                  <CreateStaffWorkPattern />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manageStaffWorkPatterns"
              element={
                <ProtectedRoute role={[UserRole.TENANT_ADMIN, UserRole.SYSTEM_ADMIN]}>
                  <ManageStaffWorkPatterns />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manageStaffWorkPatterns/edit/:id"
              element={
                <ProtectedRoute role={[UserRole.TENANT_ADMIN, UserRole.SYSTEM_ADMIN]}>
                  <EditStaffWorkPatternForm />
                </ProtectedRoute>
              }
            />

            <Route
              path="/createTask"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <CreateTask />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manageTasks"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <ManageTasks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks/edit/:id"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <EditTaskForm />
                </ProtectedRoute>
              }
            />
             <Route
              path="/createProject"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <CreateProject />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manageProjects"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <ManageProjects />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/edit/:id"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <EditProjectForm />
                </ProtectedRoute>
              }
            />
             
             
             <Route
              path="/createTenantUsers"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <CreateTenantUser />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manageTenantUsers"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <ManageTenantUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenantUsers/edit/:id"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <EditTenantUserForm />
                </ProtectedRoute>
              }
            />
             <Route
              path="/createPublicHoliday"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <CreatePublicHoliday />
                </ProtectedRoute>
              }
            />
            <Route
              path="/managePublicHolidays"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <ManagePublicHolidays />
                </ProtectedRoute>
              }
            />
            <Route
              path="/publicHolidays/edit/:id"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <EditPublicHolidayForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manageSOPs"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <ManageSOPs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sops/edit/:id"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <EditSOPForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sops/library"
              element={
                <ProtectedRoute role={[UserRole.TENANT_ADMIN, UserRole.SYSTEM_ADMIN]}>
                  <SopLibrary />
                </ProtectedRoute>
              }
            />
             <Route
              path="/sops/read/:id"
              element={
                <ProtectedRoute role={[UserRole.TENANT_ADMIN, UserRole.SYSTEM_ADMIN, UserRole.TENANT_STAFF]}>
                  <SopReader />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-sops"
              element={
                <ProtectedRoute role={[UserRole.TENANT_STAFF]}>
                  <MySops />
                </ProtectedRoute>
              }
            />
             <Route
              path="/staffRoles"
              element={
                <ProtectedRoute role={[UserRole.TENANT_STAFF]}>
                  <StaffRoles />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staffDashboard"
              element={
                <ProtectedRoute role={[UserRole.TENANT_STAFF]}>
                  <StaffDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/myWorkPatterns"
              element={
                <ProtectedRoute role={[UserRole.TENANT_STAFF]}>
                  <MyWorkPatterns />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sops/rejected"
              element={
                <ProtectedRoute role={[UserRole.TENANT_ADMIN, UserRole.SYSTEM_ADMIN]}>
                  <RejectedSOPApprovalsView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-sop-approvals"
              element={
                <ProtectedRoute role={[UserRole.TENANT_STAFF]}>
                  <MySOPApprovalsView />
                </ProtectedRoute>
                }
            />
            <Route
              path="/sop-approvals"
              element={
                <ProtectedRoute role={[UserRole.TENANT_ADMIN, UserRole.SYSTEM_ADMIN]}>
                  <TenantAdminSOPApprovals />
                </ProtectedRoute>
                }
            />
            <Route
              path="/manageProjects/:id/costs"
              element={
                <ProtectedRoute role={[UserRole.SYSTEM_ADMIN]}>
                  <ProjectCostView />
                </ProtectedRoute>
              }
              />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        </SnackbarProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
